import { withErrorBoundary } from '@sentry/react';
import React, { useEffect, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { useQuery, useSubscription } from 'urql';
import { assets } from '../../assets';
import DefaultScreen from '../../components/containers/default-screen';
import StyledReactSelect from '../../components/inputs/styled-react-select';
import { Fallback } from '../../components/utils/error-boundary';
import { formatDateTime } from '../../helpers/date-formatters';
import { GraphqlOperations } from '../../logic/graphql/operations';
import {
  Account,
  HistoryGraph,
  Portfolio,
  PortfolioFundamental,
  Query,
  Subscription,
} from '../../logic/graphql/types/types';
import { Paths } from '../../navigation/paths';
import History from './components/history/history';
import InstrumentList from './components/instrument-list/instrument-list';
import SumPortfolio from './components/sum-portfolio/sum-portfolio';
import { useIntl } from 'react-intl';

const Dashboard = (): JSX.Element => {
  const location = useLocation();
  const intl = useIntl();

  // MARK: - State
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [sumPortfolio, setSumPortfolio] = useState<Portfolio | undefined>(undefined);
  const [portfolioHistoryGraph, setPortfolioHistoryGraph] = useState<HistoryGraph | undefined>(
    undefined,
  );
  const [portfolioFundamentals, setPortfolioFundamentals] = useState<
    PortfolioFundamental[] | undefined
  >(undefined);
  const [historyInterval, setHistoryInterval] = useState<number>(1);

  // MARK: - Queries
  const userAccountsQueryResult = useQuery<Query>({
    query: GraphqlOperations.queries.getUserAccounts.queryString,
  })[0];

  const sumPortfolioQueryResult = useQuery<Query>({
    query: GraphqlOperations.queries.sum_portfolio.queryString,
    requestPolicy: 'cache-and-network',
  })[0];

  const [portfolioHistoryGraphQueryResult, reexecutePortfolioHistoryGraphQuery] = useQuery<Query>({
    query: GraphqlOperations.queries.portfolio_history_graph.queryString,
    variables: GraphqlOperations.queries.portfolio_history_graph.createVars(
      selectedAccount,
      historyInterval,
    ),
    requestPolicy: 'network-only',
  });

  const [portfolioFundamentalsQueryResult, reexecutePortfolioFundamentalsQuery] = useQuery<Query>({
    query: GraphqlOperations.queries.portfolio_fundamentals.queryString,
    variables: GraphqlOperations.queries.portfolio_fundamentals.createVars(selectedAccount),
    requestPolicy: 'network-only',
  });

  useEffect(() => {
    if (sumPortfolioQueryResult.data?.sum_portfolio) {
      setSumPortfolio(sumPortfolioQueryResult.data?.sum_portfolio);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sumPortfolioQueryResult.data]);

  useEffect(() => {
    if (portfolioHistoryGraphQueryResult.data?.portfolio_history_graph) {
      setPortfolioHistoryGraph(portfolioHistoryGraphQueryResult.data?.portfolio_history_graph);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioHistoryGraphQueryResult.data]);

  useEffect(() => {
    if (portfolioFundamentalsQueryResult.data?.portfolio_fundamentals) {
      setPortfolioFundamentals(portfolioFundamentalsQueryResult.data?.portfolio_fundamentals);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioFundamentalsQueryResult.data]);

  // MARK: - Subscriptions

  const handleSumPortfolioSubscription = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _subscriptionPortfolios: Portfolio[] = [],
    response: Subscription,
  ): Portfolio[] => {
    if (response && response.sum_portfolio) {
      setSumPortfolio(response.sum_portfolio);
      // refetch portfolio history & fundamentals data
      reexecutePortfolioFundamentalsQuery();
      reexecutePortfolioHistoryGraphQuery();
    }
    return [];
  };

  useSubscription(
    {
      query: GraphqlOperations.subscriptions.sum_portfolio.subscriptionString,
    },
    handleSumPortfolioSubscription,
  );

  // handling unknown pathnames ~ 404 error
  if (location.pathname !== Paths.dashboard) {
    return <Redirect to={Paths.dashboard} />;
  }

  return (
    <DefaultScreen
      background={assets.images.home_bg}
      title={intl.formatMessage({ id: 'dashboardPage.title' })}
      updatedAt={formatDateTime(sumPortfolio?.last_update)}
    >
      <SumPortfolio
        sumPortfolioSlices={sumPortfolio?.portfolio}
        sum={sumPortfolio?.sum}
        loading={sumPortfolioQueryResult.fetching && !sumPortfolio?.portfolio}
      />
      <section className="w-full">
        <StyledReactSelect
          placeholder={intl.formatMessage({ id: 'dashboardPage.accountSelect.placeholder' })}
          className="mb-6"
          type="blur"
          options={[
            { value: '', label: intl.formatMessage({ id: 'dashboardPage.accountSelect.all' }) },
            ...(userAccountsQueryResult.data?.getUser?.accounts.map((account: Account) => ({
              value: account.number,
              label: `${account.type}: ${account.number}`,
            })) || []),
          ]}
          defaultValue={{
            value: '',
            label: intl.formatMessage({ id: 'dashboardPage.accountSelect.all' }),
          }}
          bold
          onChange={(value) => setSelectedAccount(value as string)}
        />
        <History
          data={portfolioHistoryGraph}
          account={selectedAccount.toString()}
          onChangeInterval={(i) => setHistoryInterval(i)}
          loading={portfolioHistoryGraphQueryResult.fetching && !portfolioHistoryGraph}
        />
        <InstrumentList
          data={portfolioFundamentals || []}
          loading={portfolioFundamentalsQueryResult.fetching && !portfolioFundamentals}
        />
      </section>
    </DefaultScreen>
  );
};

export default withErrorBoundary(Dashboard, { fallback: Fallback });
